/** tm/components/ReportTodoEditor.tsx — 리포트 03 "해야할 일" 행 편집기
 *
 *  리포트에만 들어가는 레이어다(2026-08-15 확정). 시스템 업무(Task)와 별개라
 *  "○○기관에 전화하기" 같은 가벼운 약속도 자유롭게 적을 수 있다.
 *  칸마다 라벨을 붙이고 세로로 쌓아 가로 스크롤이 생기지 않게 한다(2026-08-16 사용자 피드백).
 *  하위 항목도 본 항목과 똑같이 추가·수정·순서변경이 된다(2026-08-17 사용자 피드백).
 *  하위 항목은 리포트에서 번호 없이 들여쓰기로만 구분되므로 no 는 빈 값으로 둔다.
 */
import { useState, useEffect, useCallback } from 'react';
import { isOpenDue, spanOf } from '../todoDue';
import { C, T, R } from './ui';
import * as rapi from '../api-report';
import type { ReportTodo } from '../api-report';

type Kind = 'owner' | 'due' | 'cost';
interface Preset { id: number; label: string; value: Record<string, unknown> }

interface Props {
  todos: ReportTodo[];
  onChange: (rows: ReportTodo[]) => void;
  onToast: (msg: string, type?: string) => void;
  consultDate?: string;   // 상위 줄 기한 묶기 기준 달(YYYY-MM-DD)
}

const OWNER_QUICK = ['임우승', '대표님', '임우승·대표님'];
const DUE_QUICK = ['요청 시', 'ASAP', '8월 말', '10월 초', '내년 1월', '벤처기업 확인 후'];

const emptyRow = (n: number): ReportTodo => ({ no: String(n), task: '', owner: '임우승', due: '요청 시', costs: [] });
/** 하위 항목 — 부모의 담당·기한을 그대로 물려받아 시작한다(대부분 같기 때문) */
type Sub = NonNullable<ReportTodo['subs']>[number];
const emptySub = (parent: ReportTodo): Sub => ({ no: '', task: '', owner: parent.owner || '', due: parent.due || '', costs: [] });
const costText = (c: { label?: string; amount: string; suffix?: string; note?: string }) =>
  [c.label, c.amount, c.suffix].filter(Boolean).join(' ') + (c.note ? ` (${c.note})` : '');

export default function ReportTodoEditor({ todos, onChange, onToast, consultDate }: Props) {
  const [presets, setPresets] = useState<Record<Kind, Preset[]>>({ owner: [], due: [], cost: [] });
  const [picker, setPicker] = useState('');            // `${rowIdx}:${kind}`

  const loadPresets = useCallback(async () => {
    const kinds: Kind[] = ['owner', 'due', 'cost'];
    const out = { owner: [], due: [], cost: [] } as Record<Kind, Preset[]>;
    await Promise.all(kinds.map(async k => {
      try { out[k] = (await rapi.getPresets(k)) as unknown as Preset[]; } catch { /* 이력 없으면 빈 목록 */ }
    }));
    setPresets(out);
  }, []);
  useEffect(() => { loadPresets(); }, [loadPresets]);

  const renumber = (rows: ReportTodo[]) => rows.map((r, i) => ({ ...r, no: String(i + 1) }));
  const upd = (i: number, patch: Partial<ReportTodo>) => onChange(todos.map((t, k) => (k === i ? { ...t, ...patch } : t)));
  const addRow = () => onChange(renumber([...todos, emptyRow(todos.length + 1)]));
  const delRow = (i: number) => onChange(renumber(todos.filter((_, k) => k !== i)));
  const move = (i: number, d: number) => {
    const j = i + d;
    if (j < 0 || j >= todos.length) return;
    const rows = [...todos];
    [rows[i], rows[j]] = [rows[j], rows[i]];
    onChange(renumber(rows));
  };

  // ── 하위 항목 ── 부모 행(i) 안의 subs 를 다룬다. no 는 쓰지 않으므로 다시 매기지 않는다.
  const subsOf = (i: number) => (todos[i] && todos[i].subs) || [];
  const addSub = (i: number) => upd(i, { subs: [...subsOf(i), emptySub(todos[i])] });
  const updSub = (i: number, si: number, patch: Partial<Sub>) =>
    upd(i, { subs: subsOf(i).map((s, k) => (k === si ? { ...s, ...patch } : s)) });
  const delSub = (i: number, si: number) => upd(i, { subs: subsOf(i).filter((_, k) => k !== si) });
  const moveSub = (i: number, si: number, d: number) => {
    const rows = [...subsOf(i)];
    const j = si + d;
    if (j < 0 || j >= rows.length) return;
    [rows[si], rows[j]] = [rows[j], rows[si]];
    upd(i, { subs: rows });
  };

  async function remember(kind: Kind, label: string, value: Record<string, unknown>) {
    if (!label.trim()) return;
    try { await rapi.addPreset(kind, label.trim(), value); loadPresets(); } catch { /* 이력 저장 실패는 무시 */ }
  }

  const inp = {
    padding: '7px 9px', borderRadius: R.sm, border: `1px solid ${C.border}`,
    fontSize: T.xs, outline: 'none', width: '100%', boxSizing: 'border-box' as const,
  };
  const lbl = { fontSize: 10, fontWeight: 700, color: C.muted, marginBottom: 3, display: 'block' };
  const chip = (active?: boolean) => ({
    padding: '3px 9px', borderRadius: 12, fontSize: 10, fontWeight: 600, cursor: 'pointer',
    border: `1px solid ${active ? C.accent : C.border}`,
    background: active ? C.accentLt : C.white, color: active ? C.accent : C.sub,
  });
  const iconBtn = {
    border: `1px solid ${C.border}`, background: C.white, borderRadius: R.sm,
    fontSize: 11, cursor: 'pointer', padding: '3px 7px', color: C.muted, flexShrink: 0,
  };

  // pkey 는 "행번호:종류" 또는 "행번호.하위번호:종류" — 본 항목과 하위 항목이 같은 방식으로 쓴다
  const Chips = ({ pkey, kind, quick, onPick }: { pkey: string; kind: Kind; quick?: string[]; onPick: (label: string, v: Record<string, unknown>) => void }) => {
    if (picker !== `${pkey}:${kind}`) return null;
    const list = presets[kind] || [];
    const seen = new Set<string>();
    const all = [...(quick || []), ...list.map(p => p.label)].filter(l => (seen.has(l) ? false : seen.add(l)));
    if (all.length === 0) return null;
    return (
      <div style={{ marginTop: 4, display: 'flex', flexWrap: 'wrap', gap: 4 }}>
        {all.map(l => {
          const p = list.find(x => x.label === l);
          return <span key={l} style={chip()} onMouseDown={e => { e.preventDefault(); onPick(l, p ? p.value : { text: l }); setPicker(''); }}>{l}</span>;
        })}
      </div>
    );
  };

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 10, marginBottom: 8 }}>
        <div style={{ fontSize: 10, color: C.muted, lineHeight: 1.6 }}>
          대표님께 <b>“앞으로 이렇게 하겠습니다”</b>라고 약속한 것을 한 줄씩 적으세요.<br />
          예) 벤처기업 확인 신청 · 청년일자리도약장려금 신청 · 기술보증기금 담당자 통화
        </div>
        <button onClick={addRow} style={{ ...chip(true), padding: '5px 12px', flexShrink: 0 }}>+ 행 추가</button>
      </div>

      {todos.length === 0 && (
        <div style={{ padding: 16, borderRadius: R.sm, border: `1px dashed ${C.border}`, background: C.bgAlt, fontSize: T.xs, color: C.muted, textAlign: 'center', lineHeight: 1.7 }}>
          아직 없습니다<br />
          맨 위 <b>[✨ 01·02·03 불러오기]</b> 로 메모에서 가져오거나 <b>[+ 행 추가]</b> 로 직접 적으세요
        </div>
      )}

      {todos.map((t, i) => (
        <div key={i} style={{ border: `1px solid ${C.border}`, borderRadius: R.sm, padding: 12, marginBottom: 8, background: C.white }}>

          <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 8 }}>
            <span style={{ fontSize: T.sm, fontWeight: 700, color: C.accent }}>{t.no}</span>
            <div style={{ flex: 1 }} />
            <button onClick={() => move(i, -1)} style={iconBtn} title="위로">↑</button>
            <button onClick={() => move(i, 1)} style={iconBtn} title="아래로">↓</button>
            <button onClick={() => delRow(i)} style={{ ...iconBtn, color: C.red, borderColor: C.red }} title="이 행 삭제">삭제</button>
          </div>

          <div style={{ marginBottom: 8 }}>
            <label style={lbl}>무엇을 <span style={{ color: C.red }}>*</span></label>
            <input value={t.task} onChange={e => upd(i, { task: e.target.value })}
              placeholder="예: 벤처기업 확인 신청" style={{ ...inp, fontWeight: 600 }} />
          </div>

          <div style={{ marginBottom: 8 }}>
            <label style={lbl}>부가 설명 <span style={{ fontWeight: 400 }}>(선택 · 업무명 뒤 회색 괄호로 나갑니다)</span></label>
            <input value={t.note || ''} onChange={e => upd(i, { note: e.target.value })}
              placeholder="예: (720만 원)" style={inp} />
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 8 }}>
            <div>
              <label style={lbl}>누가</label>
              <input value={t.owner} onChange={e => upd(i, { owner: e.target.value })}
                onFocus={() => setPicker(`${i}:owner`)}
                onBlur={e => { remember('owner', e.target.value, { text: e.target.value }); setTimeout(() => setPicker(p => (p === `${i}:owner` ? '' : p)), 150); }}
                placeholder="임우승" style={inp} />
              <Chips pkey={String(i)} kind="owner" quick={OWNER_QUICK} onPick={l => upd(i, { owner: l })} />
            </div>
            <div>
              <label style={lbl}>언제까지</label>
              <input value={t.due} onChange={e => upd(i, { due: e.target.value, dueAuto: false })}
                onFocus={() => setPicker(`${i}:due`)}
                onBlur={e => { remember('due', e.target.value, { text: e.target.value }); setTimeout(() => setPicker(p => (p === `${i}:due` ? '' : p)), 150); }}
                placeholder="요청 시" style={inp} />
              <Chips pkey={String(i)} kind="due" quick={DUE_QUICK} onPick={l => upd(i, { due: l, dueAuto: false })} />
              {/* 하위 기한이 있으면 상위 줄은 그 기간으로 나감(2026-10-26 지도사 「밑에 날짜가 입력되어 있으면 상단에 요청 시가 아니야」) */}
              {(() => {
                const span = (isOpenDue(t.due) || t.dueAuto) && t.subs && t.subs.length ? spanOf(t.subs.map(s => s.due), consultDate) : '';
                return span && span !== t.due ? <div style={{ marginTop: 4, fontSize: 12, color: '#1B3A5C', wordBreak: 'keep-all' }}>리포트에는 <b>{span}</b>로 나가요 · 세부 일정을 묶어서</div> : null;
              })()}
            </div>
          </div>

          <div>
            <label style={lbl}>소요비용 <span style={{ fontWeight: 400 }}>(선택 · 없으면 리포트에 — 로 나갑니다)</span></label>
            {(t.costs || []).map((c, ci) => (
              <div key={ci} style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 78px 1.4fr 30px', gap: 5, marginBottom: 5 }}>
                <input value={c.label || ''} onChange={e => upd(i, { costs: t.costs.map((x, k) => k === ci ? { ...x, label: e.target.value } : x) })} placeholder="항목 (착수금)" style={inp} />
                <input value={c.amount} onChange={e => upd(i, { costs: t.costs.map((x, k) => k === ci ? { ...x, amount: e.target.value } : x) })} placeholder="300만 원" style={inp} />
                <input value={c.suffix || ''} onChange={e => upd(i, { costs: t.costs.map((x, k) => k === ci ? { ...x, suffix: e.target.value } : x) })} placeholder="후불" style={inp} />
                <input value={c.note || ''} onChange={e => upd(i, { costs: t.costs.map((x, k) => k === ci ? { ...x, note: e.target.value } : x) })}
                  onBlur={() => remember('cost', costText(t.costs[ci]), t.costs[ci] as unknown as Record<string, unknown>)}
                  placeholder="비고 (공과금 별도)" style={inp} />
                <button onClick={() => upd(i, { costs: t.costs.filter((_, k) => k !== ci) })} style={{ ...iconBtn, color: C.red }}>✕</button>
              </div>
            ))}
            <div style={{ display: 'flex', gap: 5, alignItems: 'center' }}>
              <button onClick={() => upd(i, { costs: [...(t.costs || []), { amount: '' }] })} style={chip()}>+ 비용</button>
              <button onClick={() => setPicker(picker === `${i}:cost` ? '' : `${i}:cost`)} style={chip()}>이전에 쓴 비용</button>
              {(t.costs || []).length === 0 && <span style={{ fontSize: 10, color: C.muted }}>비용 없음</span>}
            </div>
            <Chips pkey={String(i)} kind="cost" onPick={(l, v) => { upd(i, { costs: [...(t.costs || []), v as unknown as { amount: string }] }); onToast(`비용 추가 · ${l}`); }} />
          </div>

          <div style={{ marginTop: 8, paddingTop: 8, borderTop: `1px solid ${C.border}` }}>
            <label style={lbl}>하위 항목 <span style={{ fontWeight: 400 }}>(선택 · 리포트에서 번호 없이 들여쓰기로 나갑니다)</span></label>

            {(t.subs || []).map((s, si) => (
              <div key={si} style={{ border: `1px solid ${C.border}`, borderRadius: R.sm, padding: 8, marginBottom: 5, background: C.bgAlt }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 5, marginBottom: 5 }}>
                  <span style={{ color: C.muted, fontSize: T.xs, flexShrink: 0 }}>↳</span>
                  <input value={s.task} onChange={e => updSub(i, si, { task: e.target.value })}
                    placeholder="예: 참여기업 신청" style={{ ...inp, fontWeight: 600 }} />
                  <button onClick={() => moveSub(i, si, -1)} style={iconBtn} title="위로">↑</button>
                  <button onClick={() => moveSub(i, si, 1)} style={iconBtn} title="아래로">↓</button>
                  <button onClick={() => delSub(i, si)} style={{ ...iconBtn, color: C.red, borderColor: C.red }} title="이 하위 항목 삭제">✕</button>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 5, paddingLeft: 16 }}>
                  <div>
                    <input value={s.owner} onChange={e => updSub(i, si, { owner: e.target.value })}
                      onFocus={() => setPicker(`${i}.${si}:owner`)}
                      onBlur={e => { remember('owner', e.target.value, { text: e.target.value }); setTimeout(() => setPicker(p => (p === `${i}.${si}:owner` ? '' : p)), 150); }}
                      placeholder="누가" style={inp} />
                    <Chips pkey={`${i}.${si}`} kind="owner" quick={OWNER_QUICK} onPick={l => updSub(i, si, { owner: l })} />
                  </div>
                  <div>
                    <input value={s.due} onChange={e => updSub(i, si, { due: e.target.value })}
                      onFocus={() => setPicker(`${i}.${si}:due`)}
                      onBlur={e => { remember('due', e.target.value, { text: e.target.value }); setTimeout(() => setPicker(p => (p === `${i}.${si}:due` ? '' : p)), 150); }}
                      placeholder="언제까지" style={inp} />
                    <Chips pkey={`${i}.${si}`} kind="due" quick={DUE_QUICK} onPick={l => updSub(i, si, { due: l })} />
                  </div>
                </div>
              </div>
            ))}

            <button onClick={() => addSub(i)} style={chip()}>+ 하위 항목</button>
            {(t.subs || []).length === 0 && <span style={{ fontSize: 10, color: C.muted, marginLeft: 6 }}>없음</span>}
          </div>
        </div>
      ))}
    </div>
  );
}
